import {
  UntypedFormArray,
  UntypedFormControl,
  Validators
} from '@angular/forms'
import {
  InitiativeCategory,
  getCategoryKey
} from '@common/enums/policy-initiative/initiative-category.enum'
import { ResourceMode } from '../../../../abacus/enums/resource-mode.enum'
import { Field } from '../../../../abacus/interfaces/field.interface'
import { BudgetAvailability } from '../../../../common/enums/policy-initiative/budget-availability.enum'
import {
  ALL_CATEGORY_OPTIONS,
  getMainCategory
} from '../fields-definition/category-fields'
import { getStep1ConditionForm } from '../fields-definition/form-definition'
import { PolicyInitiativeCreateEditComponent } from '../policy-initiative-create-edit.component'

// General functions
export const setCategoryField = (
  context: PolicyInitiativeCreateEditComponent,
  category: InitiativeCategory
) => {
  const categoryField: Field = context.getFieldById('category')
  context.setFieldValue(categoryField, category)
}

// Generic helper function to update field validation
const updateFieldValidation = async (
  context: PolicyInitiativeCreateEditComponent,
  fieldId: string,
  options?: {
    resetValue?: boolean
    selectOptions?: any[]
  }
) => {
  const field: Field = context.getFieldById(fieldId)
  if (field) {
    if (options?.selectOptions !== undefined) {
      field.selectOptions = options.selectOptions
    }

    const shouldReset =
      options?.resetValue !== false && context.mode !== ResourceMode.Edit
    if (shouldReset) {
      context.setFieldValue(field, null)
      context.form.get(field.property)?.reset(null)
    }

    field.hidden = false

    if (context.mode !== ResourceMode.Edit) {
      field.required = true
      field.validators = [Validators.required]
    } else {
      if (!field.validators || field.validators.length === 0) {
        field.validators = field.required ? [Validators.required] : []
      }
    }

    const formControl = context.form.get(field.property)
    if (formControl) {
      formControl.setValidators(field.validators)
      formControl.updateValueAndValidity()
    }
    context.form.updateValueAndValidity()
  }
}

export const updateCommonFieldsValidation = async (
  context: PolicyInitiativeCreateEditComponent,
  category: InitiativeCategory | null = null
) => {
  const allOptions = await context.componentResourceService.getInitiativeTypes(
    getCategoryKey(category) as InitiativeCategory
  )

  let initiativeNationalStrPredefinedValue = null
  if (
    category === InitiativeCategory.NationalStrategy &&
    context.mode !== ResourceMode.Edit
  ) {
    initiativeNationalStrPredefinedValue = 14
  }

  await updateFieldValidation(context, 'initiativeType', {
    resetValue:
      context.mode !== ResourceMode.Edit &&
      (initiativeNationalStrPredefinedValue ? false : true),
    selectOptions: allOptions
  })

  if (initiativeNationalStrPredefinedValue) {
    const initiativeTypeField = context.getFieldById('initiativeType')
    context.setFieldValue(
      initiativeTypeField,
      allOptions.find((opt) => opt.id === initiativeNationalStrPredefinedValue)
    )

    const formControl = context.form.get('initiativeTypeId')
    if (formControl) {
      formControl.setValue(String(initiativeNationalStrPredefinedValue))
      formControl.updateValueAndValidity()
    }
  }
}

export const updateInitiativeTypeOptions = async (
  context: PolicyInitiativeCreateEditComponent,
  enumValue: InitiativeCategory | null
) => {
  await updateCommonFieldsValidation(context, enumValue)
}

export const updateAiPrinciplesValidation = async (
  context: PolicyInitiativeCreateEditComponent
) => {
  await updateFieldValidation(context, 'aiPrinciples')
}

// Common Fields (For all 6 forms)
const commonFieldIds: string[] = [
  'englishName',
  'originalName',
  'sourceFiles',
  'initiativeType',
  'aiPrinciples',
  'status',
  'startYear',
  'targetSectors',
  'tags',
  'description',
  'overview'
]

// Intergovernmental Policy Frameworks
const intergovernmentalPolicyFrameworksFieldIds: string[] = [
  'intergovernmentalOrganisation',
  'extentBinding',
  'budgetAvailable'
]

// Intergovernmental Governance
const intergovernmentalGovernanceFieldIds: string[] = [
  'intergovernmentalOrganisation'
]

const conditionallyHiddenFieldIds: string[] = ['budget']

// National Behavior
const nationalStrategyFieldIds: string[] = [
  'country',
  'responsibleOrganisation',
  'engagementMechanism',
  'budgetAvailable',
  'hasMonitoringMechanism'
]

const nationalGovernanceFieldIds: string[] = ['budgetAvailable', 'country']

const nationalGeneralFieldIds: string[] = [
  'country',
  'budgetAvailable',
  'hasMonitoringMechanism'
]

const nationalRegulationsFieldIds: string[] = [
  'country',
  'extentBinding',
  'budgetAvailable'
]

export const syncBudgetFieldVisibility = (
  context: PolicyInitiativeCreateEditComponent
): void => {
  const budgetAvailable = context.form.get('budgetAvailable')
    ?.value as BudgetAvailability | null
  const amountBudgetField = context.fields.find(
    (field: Field) => field.id === 'budget'
  )

  if (!amountBudgetField) return

  const control = context.form.get(amountBudgetField.property)
  if (!control) return

  if (budgetAvailable === BudgetAvailability.Available) {
    context.showFields(['budget'])
    amountBudgetField.required = true
    amountBudgetField.validators = [
      Validators.required,
      Validators.pattern('^[0-9]*$')
    ]
    control.setValidators([Validators.required, Validators.pattern('^[0-9]*$')])
    control.updateValueAndValidity()
  } else {
    amountBudgetField.validators = []
    amountBudgetField.required = false
    control.clearValidators()
    context.setFieldValue(amountBudgetField, null)
    control.updateValueAndValidity()
    context.hideAndEmptyFields(['budget'])
  }
}

const restoreFieldValidation = async (
  context: PolicyInitiativeCreateEditComponent,
  fieldIds: string[]
) => {
  for (const fieldId of fieldIds) {
    const field: Field = context.getFieldById(fieldId)
    if (field) {
      const hasRequiredValidator = field.validators?.some(
        (v) => v === Validators.required
      )

      const isAlwaysRequiredField =
        fieldId === 'country' || fieldId === 'intergovernmentalOrganisation'

      if (field.required || hasRequiredValidator || isAlwaysRequiredField) {
        field.required = true

        if (!hasRequiredValidator) {
          field.validators = [...(field.validators || []), Validators.required]
        }
      }

      field.hidden = false

      await context.resetFieldFormControls(field)

      const formControl = context.form.get(field.property)
      if (formControl) {
        if (field.validators && field.validators.length > 0) {
          formControl.setValidators(field.validators)
        } else {
          formControl.clearValidators()
        }
        formControl.updateValueAndValidity()
      }
    }
  }
  context.form.updateValueAndValidity()
}

// Intergovernmental Behavior
export const showPolicyFrameWorkForm = async (
  context: PolicyInitiativeCreateEditComponent
) => {
  const descriptionField: Field = context.getFieldById('description')

  await context.hideAndEmptyFields([
    ...intergovernmentalGovernanceFieldIds,
    ...nationalStrategyFieldIds,
    ...nationalGovernanceFieldIds,
    ...nationalGeneralFieldIds,
    ...nationalRegulationsFieldIds,
    ...conditionallyHiddenFieldIds
  ])

  await restoreFieldValidation(context, [
    ...commonFieldIds,
    ...intergovernmentalPolicyFrameworksFieldIds
  ])

  const currentCategory =
    (context.form?.get('category')?.value as InitiativeCategory) ?? null
  await updateCommonFieldsValidation(context, currentCategory)

  descriptionField.htmlHelpText = descriptionHtmlHelpTextIfInterGovernmental
  syncBudgetFieldVisibility(context)
}

export const showGovernanceForm = async (
  context: PolicyInitiativeCreateEditComponent
) => {
  const descriptionField: Field = context.getFieldById('description')

  await context.hideAndEmptyFields([
    ...intergovernmentalPolicyFrameworksFieldIds,
    ...nationalStrategyFieldIds,
    ...nationalGovernanceFieldIds,
    ...nationalGeneralFieldIds,
    ...nationalRegulationsFieldIds,
    ...conditionallyHiddenFieldIds
  ])
  await restoreFieldValidation(context, [
    ...commonFieldIds,
    ...intergovernmentalGovernanceFieldIds
  ])

  const currentCategory =
    (context.form?.get('category')?.value as InitiativeCategory) ?? null
  await updateCommonFieldsValidation(context, currentCategory)

  descriptionField.htmlHelpText = descriptionHtmlHelpTextIfInterGovernmental
  syncBudgetFieldVisibility(context)
}

// Conditional help text for the description field.
const descriptionHtmlHelpTextIfInterGovernmental: string = `
<div class="content is-small">  
    <p>
      This explanation should describe the AI initiative in about three short and simple sentences. This is one of the most important fields. The explanation should:
    </p>
    <ul>
        <li>Use clear and succinct language</li>
        <li>Be able to be understood by someone who is not familiar with the sector or initiative</li>
        <li>Set the context for policymakers and other users who are reading about the initiative</li>
        <li>Use simple, not sector-specific terminology (avoiding idioms or "buzz" words)</li>
    </ul>
    <p>The explanation should describe:</p>
    <ul>
        <li>What are the objectives of the strategy?</li>
        <li>What are the pillars of the strategy and what sectors does it focus on (e.g., public sector, health, agriculture)?</li>
    </ul>
    </div>
`
const descriptionHtmlHelpTextIfNational: string = `
  <p>
    This explanation should describe the AI initiative in about three short and simple sentences. This is one of the most important fields. The explanation should:
  </p>
  <ul>
    <li>use clear and succinct language</li>
    <li>be able to be understood by someone who is not familiar with the sector or initiative</li>
    <li>set the context for policymakers and other users who are reading about the initiative</li>
    <li>use simple, not sector-specific terminology (avoiding idioms or "buzz" words)</li>
  </ul>
  <p>The explanation should describe:</p>
  <ul>
    <li>What are the objectives of the strategy?</li>
    <li>What are the pillars of the strategy and what sectors does it focus on (e.g., public sector, health, agriculture)?</li>
  </ul>
`

export const showCategoryOptionField = async (
  context: PolicyInitiativeCreateEditComponent
) => {
  context.showFields(['category'])
}

// National Forms

export const showNationalGeneralForm = async (
  context: PolicyInitiativeCreateEditComponent
) => {
  await context.hideAndEmptyFields([
    ...nationalGovernanceFieldIds,
    ...nationalStrategyFieldIds,
    ...nationalRegulationsFieldIds,
    ...intergovernmentalGovernanceFieldIds,
    ...intergovernmentalPolicyFrameworksFieldIds,
    ...conditionallyHiddenFieldIds
  ])

  await restoreFieldValidation(context, [
    ...commonFieldIds,
    ...nationalGeneralFieldIds
  ])

  const currentCategory =
    (context.form?.get('category')?.value as InitiativeCategory) ?? null
  await updateCommonFieldsValidation(context, currentCategory)
  syncBudgetFieldVisibility(context)
}

export const showNationalGovernanceForm = async (
  context: PolicyInitiativeCreateEditComponent
) => {
  await context.hideAndEmptyFields([
    ...nationalGeneralFieldIds,
    ...nationalStrategyFieldIds,
    ...nationalRegulationsFieldIds,
    ...intergovernmentalGovernanceFieldIds,
    ...intergovernmentalPolicyFrameworksFieldIds,
    ...conditionallyHiddenFieldIds
  ])

  await restoreFieldValidation(context, [
    ...commonFieldIds,
    ...nationalGovernanceFieldIds
  ])

  const currentCategory =
    (context.form?.get('category')?.value as InitiativeCategory) ?? null
  await updateCommonFieldsValidation(context, currentCategory)
  syncBudgetFieldVisibility(context)
}

export const showNationalStrategyForm = async (
  context: PolicyInitiativeCreateEditComponent
) => {
  await context.hideAndEmptyFields([
    ...nationalGovernanceFieldIds,
    ...nationalGeneralFieldIds,
    ...nationalRegulationsFieldIds,
    ...intergovernmentalGovernanceFieldIds,
    ...intergovernmentalPolicyFrameworksFieldIds,
    ...conditionallyHiddenFieldIds
  ])

  await restoreFieldValidation(context, [
    ...commonFieldIds,
    ...nationalStrategyFieldIds
  ])

  const currentCategory =
    (context.form?.get('category')?.value as InitiativeCategory) ?? null
  await updateCommonFieldsValidation(context, currentCategory)
  syncBudgetFieldVisibility(context)
}

export const showNationalRegulationsForm = async (
  context: PolicyInitiativeCreateEditComponent
) => {
  await context.hideAndEmptyFields([
    ...nationalGovernanceFieldIds,
    ...nationalGeneralFieldIds,
    ...nationalStrategyFieldIds,
    ...intergovernmentalGovernanceFieldIds,
    ...intergovernmentalPolicyFrameworksFieldIds,
    ...conditionallyHiddenFieldIds
  ])

  await restoreFieldValidation(context, [
    ...commonFieldIds,
    ...nationalRegulationsFieldIds
  ])

  const currentCategory =
    (context.form?.get('category')?.value as InitiativeCategory) ?? null
  await updateCommonFieldsValidation(context, currentCategory)
  syncBudgetFieldVisibility(context)
}
export const setStep1ConditionalForm = async (
  context: PolicyInitiativeCreateEditComponent,
  item: any
) => {
  const derivedMainCategory = getMainCategory(item.category)
  const mainCategoryField: Field = context.getFieldById('mainCategory')
  context.setFieldValue(mainCategoryField, derivedMainCategory)

  const categoryField: Field = context.getFieldById('category')
  if (categoryField) {
    categoryField.selectOptions = JSON.parse(
      JSON.stringify(ALL_CATEGORY_OPTIONS)
    ).filter((option) => option.parent === derivedMainCategory)
    await showCategoryOptionField(context)
    context.setFieldValue(categoryField, item.category)
  }
  await getStep1ConditionForm(item.category, context)

  if (context.mode === ResourceMode.Edit) {
    const isEmptyValue = (v: any): boolean =>
      v === null ||
      v === undefined ||
      v === '' ||
      (Array.isArray(v) && v.length === 0)

    Object.keys(context.form.controls).forEach((controlName: string) => {
      const control = context.form.get(controlName)
      if (!control) return
      const formVal = control.value
      const itemVal = (item as any)[controlName]
      let valueToApply = isEmptyValue(formVal) ? itemVal : formVal
      if (isEmptyValue(valueToApply)) return

      const targetField = context.resolvedFields.find(
        (f: Field) => f?.property === controlName
      )

      // Handle FormArray differently
      if (control instanceof UntypedFormArray) {
        if (Array.isArray(valueToApply)) {
          // Replace the FormArray with a new one containing FormControls for each value
          context.form.setControl(
            controlName,
            new UntypedFormArray(
              valueToApply.map((v) => new UntypedFormControl(v))
            )
          )
          // Get the updated control to set validators
          const updatedControl = context.form.get(controlName)
          if (updatedControl && targetField && targetField.validators) {
            updatedControl.setValidators(targetField.validators)
            updatedControl.updateValueAndValidity()
          }
        }
      } else {
        if (targetField && targetField.validators) {
          control.setValidators(targetField.validators)
        }

        control.setValue(valueToApply, { emitEvent: false })
        control.updateValueAndValidity()
      }

      if (targetField) {
        targetField.initialValue = valueToApply
        targetField.forcedValue = valueToApply
      }
    })
    context.form.updateValueAndValidity()
    syncBudgetFieldVisibility(context)
  }
}
